// ─────────────────────────────────────────────────────────────────────────────
// The MTN Shop — AI Social Media Hub (/SocialPopAdmin)
// Standalone premium dark admin: AI Brain training, Product Intelligence,
// type-driven Campaign Studio (product / brand / sale / theme / promotion
// campaigns with a 5-step forward/backward wizard) and the six-step
// Social Media Hub (shared Brand Studio engine, isolated mtnshop realm).
// Login: ShopSocialAdmin / ShopSocialAdmin

// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import {
  Brain, LayoutDashboard, Package, CalendarRange, LogOut, Sparkles, Search,
  Star, Check, X, Pencil, Globe, Folder,
  ChevronRight, Zap, Activity, ShieldCheck, TrendingUp, Instagram, Menu,
} from 'lucide-react';

import { supabase } from '@/lib/supabase';
import {
  mtnLogin, mtnIsAuthed, mtnLogout, activateMtnRealm, MTN_BRAND, BRAIN_KINDS,
  VoiceProfile, DEFAULT_VOICE, MtnProduct, MtnWeek, MtnPost, MtnBrainInput,
  MtnLearningEvent, pj, fetchProducts, fetchWeeks, fetchPosts, fetchBrainInputs,
  fetchLearningEvents, fetchVoice, saveVoice, logEvent, computeConfidence,
  campaignTypeOf,
} from '@/lib/mtnshop';

import { Glass, SectionTitle, GButton, Pill, StatCard, ProgressBar, Field, inputCls, Spinner } from '@/components/mtnshop/MtnUi';
import NavDrawer from '@/components/mtnshop/NavDrawer';
import CampaignStudio from '@/components/mtnshop/CampaignStudio';
import BrandStudio from '@/components/zera/BrandStudio';



type Tab = 'dashboard' | 'hub' | 'brain' | 'products' | 'campaigns';


// ═══════════════════════════ LOGIN ═══════════════════════════
function Login({ onIn }: { onIn: () => void }) {
  const [u, setU] = useState(''); const [p, setP] = useState(''); const [err, setErr] = useState('');
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.08),transparent_60%)]" />
      <Glass className="relative w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <img src={MTN_BRAND.avatar} alt="MTN" className="h-16 w-16 rounded-2xl object-cover mb-4" />
          <h1 className="text-2xl font-semibold text-white tracking-tight">The MTN Shop</h1>
          <p className="text-sm text-zinc-500 mt-1">AI Social Media Hub</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (mtnLogin(u, p)) onIn(); else setErr('Invalid credentials'); }} className="space-y-4">
          <Field label="Username"><input className={inputCls} value={u} onChange={e => setU(e.target.value)} placeholder="Username" autoFocus /></Field>
          <Field label="Password"><input className={inputCls} type="password" value={p} onChange={e => setP(e.target.value)} placeholder="Password" /></Field>
          {err && <p className="text-xs text-red-400">{err}</p>}
          <GButton type="submit" className="w-full py-3">Sign in to the Hub</GButton>
        </form>
      </Glass>
    </div>
  );
}

// ═══════════════════════════ DASHBOARD ═══════════════════════════
function Dashboard({ products, inputs, posts, weeks, events, voiceTrained, go }: {
  products: MtnProduct[]; inputs: MtnBrainInput[]; posts: MtnPost[]; weeks: MtnWeek[];
  events: MtnLearningEvent[]; voiceTrained: boolean; go: (t: Tab) => void;
}) {
  const approved = posts.filter(p => p.status === 'approved').length;
  const rejected = posts.filter(p => p.status === 'rejected').length;
  const withImages = posts.filter(p => p.image_url).length;
  const confidence = computeConfidence({ products: products.length, inputs: inputs.length, approved, rejected, voiceTrained });
  const productProgress = Math.min(100, Math.round((products.length / 1000) * 100));
  const voiceProgress = Math.min(100, (voiceTrained ? 60 : 20) + inputs.length * 2);
  const trending = products.filter(p => p.favorite).slice(0, 5);
  const recent = products.slice(0, 5);
  return (
    <div className="space-y-8">
      <SectionTitle kicker="Command Center" title="AI Campaign Dashboard"
        desc="A live view of everything the AI Brain knows, what it has produced, and how confident it is." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="AI Confidence" value={`${confidence}%`} sub="Grows with every training" icon={<Brain className="h-5 w-5" />} />
        <StatCard label="Products Learned" value={products.length.toLocaleString()} sub={`${products.filter(p => p.status === 'approved').length} approved`} icon={<Package className="h-5 w-5" />} />
        <StatCard label="Generated Content" value={posts.length} sub={`${approved} approved · ${rejected} rejected`} icon={<Sparkles className="h-5 w-5" />} />
        <StatCard label="Campaigns" value={weeks.length} sub={weeks[0] ? `Latest: ${campaignTypeOf(weeks[0].campaign_type).label} — ${weeks[0].theme}` : 'None yet'} icon={<CalendarRange className="h-5 w-5" />} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Glass className="p-5 space-y-4">
          <p className="text-sm font-semibold text-white flex items-center gap-2"><Activity className="h-4 w-4 text-sky-400" /> Learning Progress</p>
          <div><div className="flex justify-between text-xs text-zinc-400 mb-1.5"><span>Product intelligence</span><span>{productProgress}%</span></div><ProgressBar value={productProgress} /></div>
          <div><div className="flex justify-between text-xs text-zinc-400 mb-1.5"><span>Brand voice</span><span>{voiceProgress}%</span></div><ProgressBar value={voiceProgress} tone="emerald" /></div>
          <div><div className="flex justify-between text-xs text-zinc-400 mb-1.5"><span>Image library</span><span>{withImages} generated</span></div><ProgressBar value={Math.min(100, withImages * 7)} /></div>
          <div className="pt-2 flex items-center gap-2 text-xs text-emerald-300"><ShieldCheck className="h-4 w-4" /> System healthy — all services online</div>
        </Glass>
        <Glass className="p-5">
          <p className="text-sm font-semibold text-white flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-sky-400" /> Trending & Recent Products</p>
          <div className="space-y-2">
            {(trending.length ? trending : recent).map(p => (
              <div key={p.id} className="flex items-center gap-2 text-sm">
                {p.favorite ? <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 shrink-0" /> : <Package className="h-3.5 w-3.5 text-zinc-600 shrink-0" />}
                <span className="text-zinc-300 truncate">{p.name}</span>
              </div>
            ))}
            {products.length === 0 && <p className="text-xs text-zinc-500">Run the crawler in Product Intelligence to index shopmtn.com.</p>}
          </div>
          <GButton variant="ghost" className="mt-4 w-full" onClick={() => go('products')}>Open Product Intelligence <ChevronRight className="h-4 w-4" /></GButton>
        </Glass>
        <Glass className="p-5">
          <p className="text-sm font-semibold text-white flex items-center gap-2 mb-3"><Zap className="h-4 w-4 text-sky-400" /> Recent AI Activity</p>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {events.map(e => (
              <div key={e.id} className="text-xs">
                <p className="text-zinc-300 leading-snug">{e.detail}</p>
                <p className="text-zinc-600 mt-0.5">{new Date(e.created_at).toLocaleString()}</p>
              </div>
            ))}
            {events.length === 0 && <p className="text-xs text-zinc-500">No activity yet. Train the brain or generate a week.</p>}
          </div>
        </Glass>
      </div>
      <Glass className="p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="flex-1">
          <p className="text-white font-semibold">Build a focused campaign</p>
          <p className="text-sm text-zinc-400">Pick a product, brand story, sale, theme or promotion and the AI Brain will craft a connected campaign of feed posts + stories in that style.</p>
        </div>
        <GButton onClick={() => go('campaigns')}><Sparkles className="h-4 w-4" /> Open Campaign Studio</GButton>
      </Glass>
    </div>
  );
}

// ═══════════════════════════ AI BRAIN ═══════════════════════════
function BrainTab({ inputs, events, refresh }: { inputs: MtnBrainInput[]; events: MtnLearningEvent[]; refresh: () => void }) {
  const [kind, setKind] = useState(BRAIN_KINDS[0].id);
  const [title, setTitle] = useState(''); const [content, setContent] = useState(''); const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [voice, setVoice] = useState<VoiceProfile>(DEFAULT_VOICE);
  const [voiceLoaded, setVoiceLoaded] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);

  useEffect(() => { fetchVoice().then(v => { setVoice(v); setVoiceLoaded(true); }); }, []);

  const addInput = async () => {
    if (!title.trim() && !content.trim() && !url.trim()) return;
    setSaving(true);
    await supabase.from('mtn_shop_brain_inputs').insert({ kind, title: title || null, content: content || null, url: url || null });
    await logEvent('train', `Admin trained the brain with a ${BRAIN_KINDS.find(k => k.id === kind)?.label || kind}: ${title || url || content.slice(0, 60)}`);
    setTitle(''); setContent(''); setUrl(''); setSaving(false); refresh();
  };

  const del = async (id: string) => { await supabase.from('mtn_shop_brain_inputs').delete().eq('id', id); refresh(); };

  const slider = (label: string, key: keyof VoiceProfile) => (
    <div>
      <div className="flex justify-between text-xs text-zinc-400 mb-1"><span>{label}</span><span>{voice[key] as number}%</span></div>
      <input type="range" min={0} max={100} value={voice[key] as number}
        onChange={e => setVoice({ ...voice, [key]: Number(e.target.value) })}
        className="w-full accent-sky-400" />
    </div>
  );

  return (
    <div className="space-y-8">
      <SectionTitle kicker="Long-term Knowledge Base" title="AI Brain Training"
        desc="Feed the brain products, promotions, links, examples and voice notes. Everything you add is used as context for every future generation." />
      <div className="grid lg:grid-cols-2 gap-4">
        <Glass className="p-6 space-y-4">
          <p className="text-sm font-semibold text-white">Add Training Input</p>
          <Field label="Input type">
            <select className={inputCls} value={kind} onChange={e => setKind(e.target.value)}>
              {BRAIN_KINDS.map(k => <option key={k.id} value={k.id} className="bg-zinc-900">{k.label}</option>)}
            </select>
            <span className="mt-1 block text-[11px] text-zinc-600">{BRAIN_KINDS.find(k => k.id === kind)?.hint}</span>
          </Field>
          <Field label="Title / name"><input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Spring rigging promo" /></Field>
          <Field label="Link (optional)"><input className={inputCls} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" /></Field>
          <Field label="Details">
            <textarea className={inputCls + ' min-h-[110px]'} value={content} onChange={e => setContent(e.target.value)}
              placeholder="Paste descriptions, captions, hashtags, audience notes — anything the AI should learn." />
          </Field>
          <GButton onClick={addInput} disabled={saving} className="w-full">{saving ? <Spinner /> : <Brain className="h-4 w-4" />} Teach the AI</GButton>
        </Glass>
        <Glass className="p-6">
          <p className="text-sm font-semibold text-white mb-3">Knowledge Base <span className="text-zinc-500 font-normal">({inputs.length} items)</span></p>
          <div className="space-y-2.5 max-h-[430px] overflow-y-auto pr-1">
            {inputs.map(i => (
              <div key={i.id} className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3">
                <div className="flex items-center justify-between gap-2">
                  <Pill tone="sky">{BRAIN_KINDS.find(k => k.id === i.kind)?.label || i.kind}</Pill>
                  <button onClick={() => del(i.id)} className="text-zinc-600 hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                </div>
                {i.title && <p className="mt-1.5 text-sm text-white font-medium">{i.title}</p>}
                {i.url && <p className="text-xs text-sky-400 truncate">{i.url}</p>}
                {i.content && <p className="mt-1 text-xs text-zinc-400 line-clamp-2">{i.content}</p>}
              </div>
            ))}
            {inputs.length === 0 && <p className="text-xs text-zinc-500">Nothing yet — every input makes generations smarter.</p>}
          </div>
        </Glass>
      </div>

      <Glass className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-sm font-semibold text-white">Brand Voice Learning</p>
            <p className="text-xs text-zinc-500">The reusable voice profile every caption, hashtag set and CTA is built from.</p>
          </div>
          <GButton onClick={async () => { setSavingVoice(true); await saveVoice(voice, 'Brand voice retrained'); setSavingVoice(false); refresh(); }} disabled={savingVoice || !voiceLoaded}>
            {savingVoice ? <Spinner /> : <Check className="h-4 w-4" />} Save & Retrain
          </GButton>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <Field label="Writing style"><textarea className={inputCls} rows={2} value={voice.writing_style} onChange={e => setVoice({ ...voice, writing_style: e.target.value })} /></Field>
            <Field label="Tone"><input className={inputCls} value={voice.tone} onChange={e => setVoice({ ...voice, tone: e.target.value })} /></Field>
            <Field label="Audience targeting"><textarea className={inputCls} rows={2} value={voice.audience} onChange={e => setVoice({ ...voice, audience: e.target.value })} /></Field>
            <Field label="Product messaging"><textarea className={inputCls} rows={2} value={voice.product_messaging} onChange={e => setVoice({ ...voice, product_messaging: e.target.value })} /></Field>
            <Field label="Company vocabulary"><input className={inputCls} value={voice.vocabulary} onChange={e => setVoice({ ...voice, vocabulary: e.target.value })} /></Field>
          </div>
          <div className="space-y-4">
            {slider('Technical language level', 'technical_level')}
            {slider('Sales language level', 'sales_level')}
            {slider('Educational style', 'educational_level')}
            {slider('Humor level', 'humor_level')}
            <Field label="Caption length">
              <select className={inputCls} value={voice.caption_length} onChange={e => setVoice({ ...voice, caption_length: e.target.value })}>
                <option value="short" className="bg-zinc-900">Short & punchy</option>
                <option value="medium" className="bg-zinc-900">Medium (60-120 words)</option>
                <option value="long" className="bg-zinc-900">Long-form storytelling</option>
              </select>
            </Field>
            <Field label="Hashtag style"><input className={inputCls} value={voice.hashtag_style} onChange={e => setVoice({ ...voice, hashtag_style: e.target.value })} /></Field>
            <Field label="Call-to-action style"><input className={inputCls} value={voice.cta_style} onChange={e => setVoice({ ...voice, cta_style: e.target.value })} /></Field>
            <Field label="Approved phrases"><input className={inputCls} value={voice.approved_phrases} onChange={e => setVoice({ ...voice, approved_phrases: e.target.value })} /></Field>
            <Field label="Words to avoid"><input className={inputCls} value={voice.avoid_words} onChange={e => setVoice({ ...voice, avoid_words: e.target.value })} /></Field>
          </div>
        </div>
      </Glass>

      <Glass className="p-6">
        <p className="text-sm font-semibold text-white mb-3">AI Learning History</p>
        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
          {events.map(e => (
            <div key={e.id} className="flex items-start gap-2 text-xs">
              <Pill tone={e.event_type === 'crawl' ? 'sky' : e.event_type === 'voice_update' ? 'emerald' : 'zinc'}>{e.event_type}</Pill>
              <p className="text-zinc-400 leading-snug flex-1">{e.detail}</p>
              <p className="text-zinc-600 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}

// ═══════════════════════════ PRODUCTS ═══════════════════════════
function ProductsTab({ products, refresh }: { products: MtnProduct[]; refresh: () => void }) {
  const [q, setQ] = useState(''); const [cat, setCat] = useState('all'); const [view, setView] = useState<'all' | 'favorites' | 'recent'>('all');
  const [crawling, setCrawling] = useState(false);
  const [open, setOpen] = useState<MtnProduct | null>(null);

  const categories = useMemo(() => Array.from(new Set(products.map(p => p.category || 'Uncategorized'))).sort(), [products]);
  const filtered = useMemo(() => {
    let list = products;
    if (view === 'favorites') list = list.filter(p => p.favorite);
    if (view === 'recent') list = [...list].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 60);
    if (cat !== 'all') list = list.filter(p => (p.category || 'Uncategorized') === cat);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter(p =>
      p.name.toLowerCase().includes(s) ||
      (p.category || '').toLowerCase().includes(s) ||
      (p.vendor || '').toLowerCase().includes(s) ||
      (p.tags || '').toLowerCase().includes(s) ||
      (p.description || '').toLowerCase().includes(s));
    return list.slice(0, 120);
  }, [products, q, cat, view]);

  const crawl = async () => {
    setCrawling(true);
    await supabase.functions.invoke('mtn-crawl-products', { body: { maxPages: 4 } });
    setCrawling(false); refresh();
  };
  const toggleFav = async (p: MtnProduct) => {
    await supabase.from('mtn_shop_products').update({ favorite: !p.favorite }).eq('id', p.id); refresh();
  };
  const approve = async (p: MtnProduct) => {
    await supabase.from('mtn_shop_products').update({ status: 'approved' }).eq('id', p.id); refresh();
  };
  const remove = async (p: MtnProduct) => {
    await supabase.from('mtn_shop_products').delete().eq('id', p.id); setOpen(null); refresh();
  };
  const rename = async (p: MtnProduct) => {
    const name = window.prompt('Rename product folder', p.name);
    if (name && name.trim()) { await supabase.from('mtn_shop_products').update({ name: name.trim() }).eq('id', p.id); refresh(); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle kicker="Website Crawler & Image Library" title="Product Intelligence"
        desc="The system crawls shopmtn.com, indexes every product and organizes scraped images into folders named after each product. Favorite a folder to prioritize it as AI image reference." />
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
          <input className={inputCls + ' pl-10'} placeholder="Search by name, category, keyword, tag, manufacturer…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <select className={inputCls + ' md:w-56'} value={cat} onChange={e => setCat(e.target.value)}>
          <option value="all" className="bg-zinc-900">All categories ({categories.length})</option>
          {categories.map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
        </select>
        <div className="flex gap-1.5">
          {(['all', 'favorites', 'recent'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`rounded-xl px-3 py-2 text-xs font-medium border transition ${view === v ? 'bg-white text-black border-white' : 'bg-white/[0.05] text-zinc-300 border-white/10 hover:bg-white/[0.1]'}`}>
              {v === 'all' ? 'All' : v === 'favorites' ? 'Favorites' : 'Recently added'}
            </button>
          ))}
        </div>
        <GButton variant="ghost" onClick={crawl} disabled={crawling}>
          {crawling ? <Spinner /> : <Globe className="h-4 w-4" />} {crawling ? 'Crawling shopmtn.com…' : 'Re-crawl shopmtn.com'}
        </GButton>
      </div>
      <p className="text-xs text-zinc-500">{products.length.toLocaleString()} products indexed · showing {filtered.length}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map(p => {
          const imgs = pj<string[]>(p.images, []);
          return (
            <Glass key={p.id} className="overflow-hidden group">
              <button className="block w-full text-left" onClick={() => setOpen(p)}>
                <div className="aspect-square bg-zinc-900 relative">
                  {imgs[0]
                    ? <img src={imgs[0]} alt={p.name} className="h-full w-full object-contain p-2 transition group-hover:scale-105" loading="lazy" />
                    : <div className="flex h-full items-center justify-center text-zinc-700"><Package className="h-8 w-8" /></div>}
                  <div className="absolute left-2 top-2 flex gap-1.5">
                    {p.status === 'approved' && <Pill tone="emerald">approved</Pill>}
                    {imgs.length > 0 && <Pill tone="zinc"><Folder className="h-3 w-3 mr-1" />{imgs.length}</Pill>}
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-[13px] font-medium text-white line-clamp-2 leading-snug">{p.name}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{p.category || 'Uncategorized'}{p.price ? ` · ${p.price}` : ''}</p>
                </div>
              </button>
              <div className="flex border-t border-white/[0.06]">
                <button onClick={() => toggleFav(p)} className="flex-1 py-2 text-zinc-400 hover:text-amber-300 flex justify-center" title="Favorite">
                  <Star className={`h-4 w-4 ${p.favorite ? 'text-amber-400 fill-amber-400' : ''}`} />
                </button>
                <button onClick={() => approve(p)} className="flex-1 py-2 text-zinc-400 hover:text-emerald-300 flex justify-center border-l border-white/[0.06]" title="Approve product">
                  <Check className="h-4 w-4" />
                </button>
                <button onClick={() => rename(p)} className="flex-1 py-2 text-zinc-400 hover:text-sky-300 flex justify-center border-l border-white/[0.06]" title="Rename folder">
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            </Glass>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <Glass className="p-10 text-center">
          <p className="text-zinc-400 text-sm">{products.length === 0 ? 'No products indexed yet — run the crawler.' : 'No products match this search.'}</p>
        </Glass>
      )}

      {/* Product folder modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setOpen(null)}>
          <Glass className="w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 bg-zinc-950/95" >
            <div onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-lg font-semibold text-white">{open.name}</p>
                  <p className="text-xs text-zinc-500">{open.category} · {open.vendor || 'MTN'} {open.price ? `· ${open.price}` : ''}</p>
                </div>
                <button onClick={() => setOpen(null)} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              {open.description && <p className="text-sm text-zinc-400 mb-4 line-clamp-4">{open.description}</p>}
              <p className="text-xs font-semibold text-zinc-300 mb-2 flex items-center gap-1.5"><Folder className="h-3.5 w-3.5 text-sky-400" /> Image folder — {open.name}</p>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-5">
                {pj<string[]>(open.images, []).map((src, i) => (
                  <img key={i} src={src} alt="" className="aspect-square rounded-lg object-contain bg-zinc-900 p-1" loading="lazy" />
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                <GButton variant="success" onClick={() => { approve(open); setOpen(null); }}><Check className="h-4 w-4" /> Approve product & images</GButton>
                <GButton variant="ghost" onClick={() => { toggleFav(open); setOpen(null); }}><Star className="h-4 w-4" /> {open.favorite ? 'Unfavorite' : 'Favorite as AI reference'}</GButton>
                <GButton variant="ghost" onClick={() => window.open(open.source_url || MTN_BRAND.website, '_blank')}><Globe className="h-4 w-4" /> View on shopmtn.com</GButton>
                <GButton variant="danger" onClick={() => remove(open)}><X className="h-4 w-4" /> Delete outdated asset</GButton>
              </div>
            </div>
          </Glass>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════ SHELL ═══════════════════════════
export default function SocialPopAdmin() {
  const [authed, setAuthed] = useState(mtnIsAuthed());
  const [tab, setTab] = useState<Tab>('dashboard');
  const [navOpen, setNavOpen] = useState(false);
  const [products, setProducts] = useState<MtnProduct[]>([]);
  const [weeks, setWeeks] = useState<MtnWeek[]>([]);
  const [posts, setPosts] = useState<MtnPost[]>([]);
  const [inputs, setInputs] = useState<MtnBrainInput[]>([]);
  const [events, setEvents] = useState<MtnLearningEvent[]>([]);
  const [voiceTrained, setVoiceTrained] = useState(false);

  const refresh = async () => {
    const [pr, wk, ps, ins, ev] = await Promise.all([
      fetchProducts(), fetchWeeks(), fetchPosts(), fetchBrainInputs(), fetchLearningEvents(),
    ]);
    setProducts(pr); setWeeks(wk); setPosts(ps); setInputs(ins); setEvents(ev);
    const { data } = await supabase.from('mtn_shop_voice_versions').select('id').limit(1);
    setVoiceTrained((data || []).length > 0);
  };

  // Activate the isolated mtnshop data realm for this tab so the Social Media
  // Hub (shared six-step Brand Studio engine) reads/writes only MTN data.
  useEffect(() => { if (authed) { activateMtnRealm(); refresh(); } }, [authed]);

  if (!authed) return <Login onIn={() => setAuthed(true)} />;

  // Single source of truth for every page selection — rendered in the
  // left-hand slide-out drawer (NavDrawer).
  const nav: { id: Tab; label: string; desc: string; icon: React.ReactNode }[] = [
    { id: 'dashboard', label: 'Dashboard', desc: 'Command center & live stats', icon: <LayoutDashboard className="h-4 w-4" /> },
    { id: 'hub', label: 'Social Media Hub', desc: 'Six-step posting engine', icon: <Instagram className="h-4 w-4" /> },
    { id: 'brain', label: 'AI Brain', desc: 'Train the knowledge base', icon: <Brain className="h-4 w-4" /> },
    { id: 'products', label: 'Product Intelligence', desc: 'Crawler & image library', icon: <Package className="h-4 w-4" /> },
    { id: 'campaigns', label: 'Campaign Studio', desc: 'Product, brand, sale, theme & promo campaigns', icon: <CalendarRange className="h-4 w-4" /> },
  ];
  const current = nav.find(n => n.id === tab);

  const logout = () => { mtnLogout(); setAuthed(false); };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", Inter, sans-serif' }}>
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(56,189,248,0.06),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(168,85,247,0.05),transparent_50%)] pointer-events-none" />

      {/* Left-hand slide-out navigation drawer — holds all page selections */}
      <NavDrawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        items={nav}
        active={tab}
        onSelect={(id) => setTab(id as Tab)}
        onLogout={logout}
      />

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/[0.07] bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 md:px-6 py-3">
          <button
            onClick={() => setNavOpen(true)}
            className="flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] p-2.5 text-zinc-300 transition hover:bg-white/[0.1] hover:text-white"
            aria-label="Open navigation menu"
          >
            <Menu className="h-4 w-4" />
          </button>
          <img src={MTN_BRAND.avatar} alt="MTN" className="h-9 w-9 rounded-xl object-cover" />
          <div className="leading-tight">
            <p className="text-sm font-semibold tracking-tight">The MTN Shop</p>
            <p className="text-[11px] text-zinc-500">AI Social Media Hub</p>
          </div>
          {/* Current page indicator */}
          {current && (
            <div className="ml-2 hidden sm:flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-1.5 text-xs font-medium text-zinc-300">
              {current.icon} {current.label}
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <a href={MTN_BRAND.instagram} target="_blank" rel="noopener noreferrer" className="hidden sm:block text-xs text-zinc-500 hover:text-white">@{MTN_BRAND.handle}</a>
            <GButton variant="ghost" className="px-3 py-1.5 text-xs" onClick={logout}>
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </GButton>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1400px] px-4 md:px-6 py-8">
        {tab === 'dashboard' && <Dashboard products={products} inputs={inputs} posts={posts} weeks={weeks} events={events} voiceTrained={voiceTrained} go={setTab} />}
        {tab === 'hub' && (
          <div className="space-y-6">
            <SectionTitle kicker="Six-Step Social Posting Engine" title="Social Media Hub"
              desc="Build the learning database, generate a week from your trained brand voice, edit posts exactly as they'll appear on Instagram, route them through administrator approval, export from the Ready Library, and connect analytics." />
            {/* Shared Brand Studio engine — identical dashboard to the flagship
                Social Media Hub, scoped to the isolated mtnshop data realm.
                The `mtn-mono` class re-skins the engine's navy & gold palette
                into this portal's sleek black & white scheme (see index.css). */}
            <div className="mtn-mono overflow-hidden rounded-3xl border border-white/[0.08] bg-white shadow-2xl">
              <BrandStudio />
            </div>
          </div>
        )}
        {tab === 'brain' && <BrainTab inputs={inputs} events={events} refresh={refresh} />}
        {tab === 'products' && <ProductsTab products={products} refresh={refresh} />}
        {tab === 'campaigns' && <CampaignStudio weeks={weeks} products={products} refresh={refresh} />}
      </main>


      <footer className="relative border-t border-white/[0.06] py-6 text-center text-xs text-zinc-600">
        The MTN Shop · AI Social Media Hub · Self-improving marketing intelligence for shopmtn.com
      </footer>
    </div>
  );
}

